import classnames from 'classnames/bind'
import React from 'react'

import Image, { ImageProps } from '~/components/Image'
import InlineCta from '~/components/InlineCta'
import LangSwitcher from '~/components/LangSwitcher'
import Link from '~/components/Link'
import RichText, { RichTextBlock } from '~/components/RichText'
import RoundedCta, { LinkProps } from '~/components/RoundedCta'

import { useGlobalData } from '~/providers/GlobalDataProvider'
import { useStyle } from '~/providers/StyleProvider'

import { Language } from '~/data/page-data/meta'

import Curve from '../Curve'
import css from './styles.module.scss'

const cx = classnames.bind(css)

interface Columns {
  title?: RichTextBlock[]
  links?: LinkProps[]
}

export interface FooterProps {
  className?: string
  columns?: Columns[]
  logo?: ImageProps
  copyright?: RichTextBlock[]
  alternateLangs?: Language[]
}
/**
 * Footer component
 * @param props
 * @returns Component
 */
function Footer({
  className,
  logo,
  copyright,
  alternateLangs,
  columns,
}: FooterProps) {
  const copyrightStyle = useStyle({ textPreset: 'text-caption' })
  const columnHeadingStyle = useStyle({
    textPreset: 'title-heading-6',
    color: 'white',
  })
  const columnLinksStyle = useStyle({ color: 'melrose' })

  const gridStyle = useStyle({ grid: 'base-grid' })

  const { redirects } = useGlobalData()

  return (
    <footer className={cx(className, css.Footer)}>
      <Curve
        className={css.curve}
        color="#0d244b"
        type="footer"
        hasGradiant={false}
      />
      <div className={css.content}>
        <div className={(css.grid, gridStyle)}>
          <div className={css.footerContent}>
            <div className={css.columns}>
              {columns?.map(({ title, links }, index) => {
                return (
                  <div className={css.column} key={`column${index}`}>
                    {title && (
                      <RichText
                        className={cx(css.columnTitle, columnHeadingStyle)}
                        render={title}
                      />
                    )}
                    <div className={css.columnLinks}>
                      {links?.map((link, subIndex) => {
                        return (
                          <div
                            className={cx(css.link, columnLinksStyle)}
                            key={`link${index}${subIndex}`}>
                            <InlineCta
                              textPreset="cta-small"
                              {...link}
                              theme="inherit"
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>

            {logo && (
              <Link
                {...redirects?.homepage}
                className={css.logo}
                title={logo?.alt}>
                <Image layout="responsive" {...logo} />
              </Link>
            )}
            {copyright && (
              <RichText
                className={cx(css.copyright, copyrightStyle)}
                render={copyright}
              />
            )}
          </div>
        </div>
      </div>
    </footer>
  )
}

Footer.defaultProps = {}

export default Footer
